// apps/office/src/pages/AbandonedCheckouts.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Eye, 
  Search, 
  RefreshCw,
  ShoppingCart,
  User,
  Clock,
  DollarSign,
  AlertCircle,
  Loader2,
  Download,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

// Constants
const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'converted', label: 'Converted', color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-800' },
];

const STEP_OPTIONS = [
  { value: 'form_filled', label: 'Form Filled', color: 'bg-gray-100 text-gray-700' },
  { value: 'payment_started', label: 'Payment Started', color: 'bg-purple-100 text-purple-700' },
  { value: 'payment_failed', label: 'Payment Failed', color: 'bg-red-100 text-red-700' },
  { value: 'payment_cancelled', label: 'Payment Cancelled', color: 'bg-orange-100 text-orange-700' },
  { value: 'payment_completed', label: 'Payment Completed', color: 'bg-green-100 text-green-700' },
];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...STATUS_OPTIONS,
];

// Helper Components
const StatusBadge = ({ status }) => {
  const option = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  return <Badge className={option.color}>{option.label}</Badge>;
};

const StepBadge = ({ step }) => {
  const option = STEP_OPTIONS.find(s => s.value === step) || STEP_OPTIONS[0];
  return <Badge variant="outline" className={option.color}>{option.label}</Badge>;
};

// Main Component
const AbandonedCheckouts = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCheckout, setSelectedCheckout] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [checkoutToDelete, setCheckoutToDelete] = useState(null);

  // Fetch abandoned checkouts
  const { data: checkouts = [], isLoading, refetch, error } = useQuery({
    queryKey: ['abandonedCheckouts', user?.id],
    queryFn: async () => {
      try {
        const records = await pb.collection('abandoned_checkouts').getFullList({
          sort: '-abandonedAt',
          expand: 'user',
        });
        return records;
      } catch (error) {
        console.error('Error fetching abandoned checkouts:', error);
        toast.error('Failed to load abandoned checkouts');
        return [];
      }
    },
    enabled: !!user,
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) =>
      pb.collection('abandoned_checkouts').update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(['abandonedCheckouts']);
      toast.success('Status updated successfully');
    },
    onError: (error) => {
      console.error('Status update error:', error);
      toast.error('Failed to update status: ' + (error.message || 'Unknown error'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('abandoned_checkouts').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['abandonedCheckouts']);
      toast.success('Checkout deleted successfully');
      setIsDeleteConfirmOpen(false);
      setCheckoutToDelete(null);
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete: ' + (error.message || 'Unknown error'));
    },
  });

  // Filter checkouts
  const filteredCheckouts = checkouts.filter((checkout) => {
    const matchesSearch = 
      checkout.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      checkout.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      checkout.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || checkout.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: checkouts.length,
    new: checkouts.filter(c => c.status === 'new').length,
    contacted: checkouts.filter(c => c.status === 'contacted').length,
    converted: checkouts.filter(c => c.status === 'converted').length,
    lost: checkouts.filter(c => c.status === 'lost').length,
    totalValue: checkouts.reduce((sum, c) => sum + (c.total || 0), 0),
  };

  // Handlers
  const handleStatusChange = (id, newStatus) => {
    updateStatusMutation.mutate({ id, status: newStatus });
  };

  const handleDelete = (id) => {
    setCheckoutToDelete(id);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (checkoutToDelete) {
      deleteMutation.mutate(checkoutToDelete);
    }
  };

  const handleViewDetails = (checkout) => {
    setSelectedCheckout(checkout);
    setIsDetailOpen(true);
  };

  const handleSendEmail = (email, name) => {
    const subject = encodeURIComponent('Complete Your Purchase - HouseDesigns99');
    const body = encodeURIComponent(
      `Hi ${name || 'Customer'},\n\n` +
      `We noticed you left items in your cart at HouseDesigns99. ` +
      `Complete your purchase today and get your dream home design! ??\n\n` +
      `Visit: https://housedesigns99.com/cart\n\n` +
      `Best regards,\nHouseDesigns99 Team`
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
        <p className="text-slate-500">Loading abandoned checkouts...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-700 font-medium">Failed to load data</p>
        <p className="text-slate-400 text-sm mt-1">{error.message}</p>
        <Button onClick={() => refetch()} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Abandoned Checkouts</h1>
          <p className="text-slate-500 text-sm mt-1">
            Track users who filled the checkout form but didn't complete payment
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetch()} 
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="w-4 h-4" /> Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-xl font-bold text-slate-800">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">New</p>
            <p className="text-xl font-bold text-blue-600">{stats.new}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Contacted</p>
            <p className="text-xl font-bold text-yellow-600">{stats.contacted}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Converted</p>
            <p className="text-xl font-bold text-green-600">{stats.converted}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Lost</p>
            <p className="text-xl font-bold text-red-600">{stats.lost}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Value</p>
            <p className="text-xl font-bold text-indigo-600">?{stats.totalValue.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-indigo-500" />
              Abandoned Carts
            </CardTitle>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36 h-9">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCheckouts.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No abandoned checkouts found</p>
              <p className="text-sm text-slate-400">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Checkouts will appear here when users abandon them'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Cart Value</TableHead>
                    <TableHead>Step</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Abandoned</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCheckouts.map((checkout) => (
                    <TableRow key={checkout.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{checkout.name}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[120px]">
                              {checkout.address?.slice(0, 30) || 'No address'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-xs">
                            <Mail className="w-3 h-3 text-slate-400" />
                            <span className="truncate max-w-[120px]">{checkout.email}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <Phone className="w-3 h-3 text-slate-400" />
                            <span>{checkout.phone || 'N/A'}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-semibold text-indigo-600">
                          ?{checkout.total?.toLocaleString() || '0'}
                        </div>
                        <div className="text-xs text-slate-400">
                          {checkout.cartItems?.length || 0} items
                        </div>
                      </TableCell>
                      <TableCell>
                        <StepBadge step={checkout.checkoutStep} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={checkout.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {checkout.abandonedAt 
                            ? format(new Date(checkout.abandonedAt), 'dd MMM yyyy')
                            : 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(checkout)}
                            className="h-8 w-8 p-0"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-slate-400" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendEmail(checkout.email, checkout.name)}
                            className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700"
                            title="Send Email"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Select
                            value={checkout.status}
                            onValueChange={(val) => handleStatusChange(checkout.id, val)}
                          >
                            <SelectTrigger className="w-[110px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(checkout.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Checkout Details</DialogTitle>
            <DialogDescription>
              Full information about this abandoned checkout
            </DialogDescription>
          </DialogHeader>
          {selectedCheckout && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-500">Name</p>
                  <p className="font-medium">{selectedCheckout.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Status</p>
                  <StatusBadge status={selectedCheckout.status} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Email</p>
                  <p className="text-sm">{selectedCheckout.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Phone</p>
                  <p className="text-sm">{selectedCheckout.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Address</p>
                  <p className="text-sm">{selectedCheckout.address || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Checkout Step</p>
                  <StepBadge step={selectedCheckout.checkoutStep} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Amount</p>
                  <p className="text-sm font-bold text-indigo-600">?{selectedCheckout.total?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">Items</p>
                  <p className="text-sm">{selectedCheckout.cartItems?.length || 0} items</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-slate-500">Cart Items</p>
                  {selectedCheckout.cartItems?.length > 0 ? (
                    <div className="mt-2 space-y-1">
                      {selectedCheckout.cartItems.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm p-2 bg-slate-50 rounded">
                          <span>{item.name || `Item ${idx + 1}`}</span>
                          <span>?{item.price?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No items in cart</p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-slate-500">Abandoned At</p>
                  <p className="text-sm">
                    {selectedCheckout.abandonedAt 
                      ? format(new Date(selectedCheckout.abandonedAt), 'dd MMM yyyy, HH:mm')
                      : 'N/A'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    handleSendEmail(selectedCheckout.email, selectedCheckout.name);
                    setIsDetailOpen(false);
                  }}
                  className="gap-2"
                >
                  <Mail className="w-4 h-4" /> Send Email
                </Button>
                <Button onClick={() => setIsDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this abandoned checkout? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <p className="text-xs text-slate-400 text-center border-t border-slate-200 pt-4">
        Abandoned checkouts are automatically tracked when users fill the checkout form
        <span className="block mt-1">Last updated: {format(new Date(), 'dd MMM yyyy, HH:mm')}</span>
      </p>
    </div>
  );
};

export default AbandonedCheckouts;